import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  ModalBody,
  ModalContent,
  ModalFooter,
  Text,
  VStack,
} from "@chakra-ui/react";
import { type TezosToolkit } from "@taquito/taquito";
import {
  type ChainFullId,
  ChainFullIds,
  type SignTransactionOptions,
  getChainAddress,
  tezToMutez,
} from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import {
  useCurrentAccount,
  useGetChainBalance,
  useGetCurrentSaplingViewingKeyAddress,
  useGetNetworkSymbol,
} from "@umami/state";
import { FormProvider } from "react-hook-form";

import { SignPage } from "./SignPage";
import { type ShieldFormValues } from "./types";
import { useEstimateShieldFee } from "./useEstimateShieldFee";
import { KnownAccountsAutocomplete } from "../../AddressAutocomplete";
import { FormPageHeader } from "../../SendFlow/FormPageHeader";

import { AddressTile } from "@/components/AddressTile";
import { SignButton } from "@/components/SendFlow/SignButton";
import { useColor } from "@/styles/useColor";

export const FormPage = ({ chainId }: { chainId: ChainFullId }) => {
  const color = useColor();
  const { openWith } = useDynamicModalContext();
  const currentAccount = useCurrentAccount();
  const currentSaplingViewingKeyAddress = useGetCurrentSaplingViewingKeyAddress();
  const symbol = useGetNetworkSymbol()(chainId);
  const { balance } = useGetChainBalance()(chainId);
  const { estimateFee, isLoading: isFeeLoading } = useEstimateShieldFee();

  const form = useMultiForm({
    mode: "onBlur",
    defaultValues: {
      shieldedAddress: currentSaplingViewingKeyAddress ?? "",
      memo: "",
      amount: "",
      chainId: ChainFullIds.TEZOS,
    },
  });

  const {
    formState: { errors },
    register,
  } = form;

  const handlePreview = async (
    formValues: ShieldFormValues,
    auth: { toolkit: TezosToolkit; options: SignTransactionOptions }
  ) => {
    const transaction = {
      to: formValues.shieldedAddress,
      amount: Number(formValues.amount),
      memo: formValues.memo,
      mutez: false,
    };

    const result = await estimateFee(transaction, auth);

    if (!result) {
      return;
    }

    await openWith(<SignPage chainId={chainId} formValues={formValues} {...result} />);
  };

  const handleValidateAmount = (value: string) => {
    const numValue = parseFloat(value);

    if (isNaN(numValue) || numValue <= 0) {
      return "Amount must be greater than 0";
    }

    const amountToSend = tezToMutez(value);

    if (amountToSend.gt(tezToMutez(balance))) {
      return "Insufficient funds";
    }

    return true;
  };

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form>
          <FormPageHeader title="Shield" />
          <ModalBody gap="24px">
            {currentAccount && (
              <FormControl>
                <FormLabel>From</FormLabel>
                <AddressTile
                  address={getChainAddress(currentAccount, chainId)}
                  balance={balance}
                  hideBalance={false}
                />
              </FormControl>
            )}
            <FormControl isInvalid={!!errors.shieldedAddress}>
              <KnownAccountsAutocomplete
                accountsType="sapling"
                allowUnknown
                inputName="shieldedAddress"
                isDisabled={isFeeLoading}
                label="Shielded address"
              />
              {errors.shieldedAddress && (
                <FormErrorMessage data-testid="shielded-address-error">
                  {errors.shieldedAddress.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.amount}>
              <FormLabel>Amount</FormLabel>
              <InputGroup>
                <Input
                  isDisabled={isFeeLoading}
                  step="0.000001"
                  type="number"
                  {...register("amount", {
                    required: "Amount is required",
                    validate: handleValidateAmount,
                  })}
                  placeholder="0.000000"
                />
                <InputRightElement marginRight="16px" color={color("400")} fontSize="16px">
                  {symbol}
                </InputRightElement>
              </InputGroup>
              {errors.amount && (
                <FormErrorMessage data-testid="amount-error">
                  {errors.amount.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.memo}>
              <FormLabel>Memo (optional)</FormLabel>
              <Input
                isDisabled={isFeeLoading}
                {...register("memo", {
                  maxLength: {
                    value: 8,
                    message: "Memo must be 8 characters or less",
                  },
                })}
                maxLength={8}
                placeholder="Optional memo"
              />
              {errors.memo && (
                <FormErrorMessage data-testid="memo-error">{errors.memo.message}</FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Box width="full">
              <VStack alignItems="center">
                {isFeeLoading && <Text>Building the transaction may take a minute or more.</Text>}
                {currentAccount && (
                  <SignButton
                    isLoading={isFeeLoading}
                    onSubmit={(tezosToolkit, options) =>
                      form.handleSubmit(values =>
                        handlePreview(values, {
                          toolkit: tezosToolkit,
                          options,
                        })
                      )()
                    }
                    signer={currentAccount}
                    text="Preview"
                  />
                )}
              </VStack>
            </Box>
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
